import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Search,
  Plus,
  FileText,
  Users,
  Building2,
  ClipboardList,
  CreditCard,
  Briefcase,
  Calculator,
  BarChart3,
  Settings,
  Landmark,
  Bell,
  FileWarning,
  ArrowRight,
  Command,
  Wallet,
  Receipt
} from 'lucide-react';

const commandGroups = [
  {
    name: 'Quick Actions',
    commands: [
      { id: 'new-client', label: 'New Client', icon: Building2, action: 'navigate', path: '/clients?action=new', shortcut: 'C' },
      { id: 'new-worker', label: 'New Worker', icon: Users, action: 'navigate', path: '/workers?action=new', shortcut: 'W' },
      { id: 'new-order', label: 'New Service Order', icon: ClipboardList, action: 'navigate', path: '/service-orders?action=new', shortcut: 'O' },
      { id: 'new-invoice', label: 'New Invoice', icon: FileText, action: 'navigate', path: '/invoices?action=new', shortcut: 'I' },
      { id: 'new-quotation', label: 'New Quotation', icon: Receipt, action: 'navigate', path: '/quotations?action=new', shortcut: 'Q' },
      { id: 'new-payment', label: 'Record Payment', icon: CreditCard, action: 'navigate', path: '/payments?action=new', shortcut: 'P' },
      { id: 'new-expense', label: 'Record Expense', icon: Wallet, action: 'navigate', path: '/expenses?action=new', shortcut: 'E' },
      { id: 'new-govt-app', label: 'New Govt Application', icon: Landmark, action: 'navigate', path: '/govt-services?action=new', shortcut: 'G' },
    ]
  },
  {
    name: 'Navigation',
    commands: [
      { id: 'go-dashboard', label: 'Dashboard', icon: BarChart3, action: 'navigate', path: '/' },
      { id: 'go-clients', label: 'Clients', icon: Building2, action: 'navigate', path: '/clients' },
      { id: 'go-workers', label: 'Workers', icon: Users, action: 'navigate', path: '/workers' },
      { id: 'go-services', label: 'Services', icon: Briefcase, action: 'navigate', path: '/services' },
      { id: 'go-orders', label: 'Service Orders', icon: ClipboardList, action: 'navigate', path: '/service-orders' },
      { id: 'go-invoices', label: 'Invoices', icon: FileText, action: 'navigate', path: '/invoices' },
      { id: 'go-payments', label: 'Payments', icon: CreditCard, action: 'navigate', path: '/payments' },
      { id: 'go-govt', label: 'Government Services', icon: Landmark, action: 'navigate', path: '/govt-services' },
      { id: 'go-expiry', label: 'Expiry Manager', icon: FileWarning, action: 'navigate', path: '/expiry-management' },
      { id: 'go-analytics', label: 'Analytics', icon: BarChart3, action: 'navigate', path: '/analytics' },
      { id: 'go-financials', label: 'Financial Reports', icon: Calculator, action: 'navigate', path: '/financial-reports' },
      { id: 'go-settings', label: 'Settings', icon: Settings, action: 'navigate', path: '/settings' },
    ]
  }
];

export default function CommandPalette({ open, onOpenChange }) {
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();

  // Filter commands based on search
  const filteredGroups = commandGroups.map(group => ({
    ...group,
    commands: group.commands.filter(cmd => 
      cmd.label.toLowerCase().includes(search.toLowerCase())
    )
  })).filter(group => group.commands.length > 0);

  // Flatten for keyboard navigation
  const allCommands = filteredGroups.flatMap(g => g.commands);

  const executeCommand = useCallback((command) => {
    if (command.action === 'navigate') {
      navigate(command.path);
    }
    onOpenChange(false);
    setSearch('');
    setSelectedIndex(0);
  }, [navigate, onOpenChange]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!open) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(i => Math.min(i + 1, allCommands.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(i => Math.max(i - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (allCommands[selectedIndex]) {
            executeCommand(allCommands[selectedIndex]);
          }
          break;
        case 'Escape':
          onOpenChange(false);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, selectedIndex, allCommands, executeCommand, onOpenChange]);

  // Reset selection when search changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [search]);

  let commandIndex = -1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 max-w-xl overflow-hidden" data-testid="command-palette">
        <div className="flex items-center border-b px-4 py-3">
          <Search className="w-5 h-5 text-muted-foreground mr-3" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Type a command or search..."
            className="border-0 focus-visible:ring-0 text-base placeholder:text-muted-foreground/60"
            autoFocus
            data-testid="command-search"
          />
          <Badge variant="outline" className="ml-2 text-xs font-normal">
            <Command className="w-3 h-3 mr-1" />K
          </Badge>
        </div>

        <div className="max-h-[400px] overflow-y-auto p-2">
          {filteredGroups.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Search className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p>No commands found</p>
            </div>
          ) : (
            filteredGroups.map((group) => (
              <div key={group.name} className="mb-3">
                <p className="text-xs font-medium text-muted-foreground px-2 py-1 uppercase tracking-wider">
                  {group.name}
                </p>
                {group.commands.map((cmd) => {
                  commandIndex++;
                  const isSelected = commandIndex === selectedIndex;
                  const Icon = cmd.icon;
                  
                  return (
                    <button
                      key={cmd.id}
                      onClick={() => executeCommand(cmd)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                        isSelected 
                          ? 'bg-slate-100 text-slate-900' 
                          : 'hover:bg-slate-50 text-slate-700'
                      }`}
                      data-testid={`cmd-${cmd.id}`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        isSelected ? 'bg-teal-100 text-teal-700' : 'bg-slate-100 text-slate-500'
                      }`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <span className="flex-1 font-medium">{cmd.label}</span>
                      {cmd.shortcut && (
                        <Badge variant="outline" className="text-xs font-normal">
                          {cmd.shortcut}
                        </Badge>
                      )}
                      <ArrowRight className={`w-4 h-4 transition-transform ${isSelected ? 'translate-x-1' : ''}`} />
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div className="border-t px-4 py-2 flex items-center justify-between text-xs text-muted-foreground bg-slate-50">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-white border rounded text-[10px]">↑↓</kbd> Navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-white border rounded text-[10px]">Enter</kbd> Select
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-white border rounded text-[10px]">Esc</kbd> Close
            </span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
